import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../styles/signup.css";
import {
  CustomSwal,
  showOtpPrompt,
  maskEmail,
  maskMobile,
} from "../services/customSwal";
import {
  getStaffById,
  sendSignupOtp,
  verifySignupOtp,
  insertUser,
  checkLoginIdExists
} from "../api/signupApiService";

// Lock helpers
const LOCK_DURATION = 30 * 60 * 1000; // 30 minutes

const isLocked = (staffId: string) => {
  const data = localStorage.getItem(`signup_lock_${staffId}`);
  if (!data) return false;
  const { lockedAt } = JSON.parse(data);
  const now = Date.now();
  if (now - lockedAt < LOCK_DURATION) return true;
  localStorage.removeItem(`signup_lock_${staffId}`);
  return false;
};

const setLock = (staffId: string) => {
  localStorage.setItem(
    `signup_lock_${staffId}`,
    JSON.stringify({ lockedAt: Date.now() })
  );
};


const Signup: React.FC = () => {
  const [form, setForm] = useState({
    staffId: "",
    staffName: "",
    loginId: "",
    instId: "",
    department: "",
    designation: "",
    email: "",
    mobileNo: "",
    password: "",
    repassword: "",
  });

  const [actualEmail, setActualEmail] = useState("");
  const [actualMobile, setActualMobile] = useState("");
  const [instId, setInstId] = useState("");

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: boolean }>({});

  // OTP caching
  const [otpCache, setOtpCache] = useState<{
    type: string;
    value: string;
    timestamp: number;
  } | null>(null);

  // Store verified OTPs
  const [verifiedEmailOtp, setVerifiedEmailOtp] = useState("");
  const [verifiedMobileOtp, setVerifiedMobileOtp] = useState("");

  const navigate = useNavigate();

  const handleChange = (field: keyof typeof form, value: string) => {
    setForm({ ...form, [field]: value });
    if (errors[field]) setErrors({ ...errors, [field]: false });
  };

  const fetchStaffDetails = async () => {
    try {
      const staffId = form.staffId.trim();

      // Check lock
      if (isLocked(staffId)) {
        await CustomSwal.fire(
          "Blocked",
          "Too many failed attempts. Please try again after some time.",
          "error"
        );
        return;
      }
      const data = await getStaffById(form.staffId);
      if (data.status === "1" && data.Data?.length) {
        const staff = data.Data[0];
        setForm({
          ...form,
          staffName: staff.staff_name || "",
          instId: staff.inst_name || "",
          department: staff.user_dept || "",
          designation: staff.user_desig || "",
          email: maskEmail(staff.email_id) || "",
          mobileNo: maskMobile(staff.mobile) || "",
        });
        setActualEmail(staff.email_id || "");
        setActualMobile(staff.mobile || "");
        setInstId(staff.inst_id || "");
      } else {
        CustomSwal.fire(
          "Not Found",
          "Staff ID not recognized. Please contact administrator.",
          "warning"
        );
      }
    } catch (err) {
      console.error("Error fetching staff:", err);
      CustomSwal.fire("Error", "Unable to fetch staff details", "error");
    }
  };

  const passwordsMatch = form.password === form.repassword;
  const passwordTooShort = form.password.length < 6;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormSubmitted(true);

    // Validation
    const newErrors: { [key: string]: boolean } = {};
    if (!form.staffId) newErrors.staffId = true;
    if (!form.staffName) newErrors.staffName = true;
    if (!form.instId) newErrors.instId = true;
    if (!form.loginId) newErrors.loginId = true;
    if (!form.department) newErrors.department = true;
    if (!form.designation) newErrors.designation = true;
    if (!form.email) newErrors.email = true;
    if (!form.mobileNo) newErrors.mobileNo = true;
    if (!form.password || passwordTooShort) newErrors.password = true;
    if (!form.repassword || !passwordsMatch) newErrors.repassword = true;

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    const staffId = form.staffId.trim();

    // Check lock
    if (isLocked(staffId)) {
      await CustomSwal.fire(
        "Blocked",
        "Too many failed attempts. Please try again after some time.",
        "error"
      );
      return;
    }

    try {
      // -----------------------
      // Step 1: Confirm Identity
      // -----------------------
      // Step 1: Confirm Identity (max 3 attempts)
      let confirmAttempts = 0;
      let type: 'email' | 'mobile' | null = null;
      let entered: string = '';

      while (confirmAttempts < 3) {
        const { value: confirmValue } = await CustomSwal.fire({
          title: 'Confirm Identity',
          input: 'text',
          inputLabel: 'Enter your registered Email or Mobile',
          inputPlaceholder: 'Email / Mobile',
          showCancelButton: true,
          allowOutsideClick: false,
          allowEscapeKey: false,
        });

        if (!confirmValue) {
          CustomSwal.fire('Cancelled', 'Confirmation is required.', 'warning');
          return;
        }

        entered = confirmValue.trim();

        if (/^\d+$/.test(entered)) {
          type = 'mobile';
          if (entered === actualMobile) break;
        } else {
          type = 'email';
          if (entered.toLowerCase() === actualEmail.toLowerCase()) break;
        }

        confirmAttempts++;
        if (confirmAttempts < 3) {
          await CustomSwal.fire(
            'Mismatch',
            `Does not match registered. Attempts left: ${3 - confirmAttempts}`,
            'error'
          );
        } else {
          await CustomSwal.fire(
            'Blocked',
            'Maximum attempts reached. Try again after some time.',
            'error'
          );
          setLock(staffId);
          return;
        }
      }


      // -----------------------
      // Step 2: OTP
      // -----------------------
      const now = Date.now();
      if (
        otpCache &&
        otpCache.type === type &&
        otpCache.value === entered &&
        now - otpCache.timestamp < 5 * 60 * 1000
      ) {
        await CustomSwal.fire(
          "Warning",
          "Please try again later.",
          "warning"
        );
        return;
      }

      const otpSendRes = await sendSignupOtp(type!, entered!);
      console.log("otpSendRes", otpSendRes);
      if (!otpSendRes || otpSendRes.result !== "1") {
        await CustomSwal.fire("Warning", "Try again after some time.", "warning");
        return;
      }
      setOtpCache({ type: type!, value: entered!, timestamp: now });

      let otpAttempts = 0;
      let otpVerified = false;
      let otpValue = "";

      while (otpAttempts < 3 && !otpVerified) {
        const otp = await showOtpPrompt(
          type === "mobile" ? entered! : undefined,
          type === "email" ? entered! : undefined
        );

        if (!otp) {
          await CustomSwal.fire(
            "Cancelled",
            "OTP verification is required.",
            "warning"
          );
          return;
        }

        const otpVerifyRes = await verifySignupOtp(type!, entered!, otp);
        console.log("otpVerifyRes", otpVerifyRes);

        if (otpVerifyRes && otpVerifyRes.result === '1') {
          otpVerified = true;
          otpValue = otp;
          break;
        } else {
          otpAttempts++;
          if (otpAttempts < 3) {
            await CustomSwal.fire(
              "Invalid OTP",
              `Incorrect OTP. Attempts left: ${3 - otpAttempts}`,
              "error"
            );
          } else {
            await CustomSwal.fire(
              "Blocked",
              "Maximum OTP attempts reached. Try again after some time.",
              "error"
            );
            setLock(staffId);
            return;
          }
        }
      }

      // ✅ Save OTPs
      let emailOtpValue = verifiedEmailOtp;
      let mobileOtpValue = verifiedMobileOtp;

      if (type === "email") emailOtpValue = otpValue;
      if (type === "mobile") mobileOtpValue = otpValue;

      // -----------------------
      // Step 3: Save User
      // -----------------------
      const payload = {
        staff_id: form.staffId,
        user_name: form.staffName,
        inst_id: instId,
        login_id: form.loginId,
        user_dept: form.department,
        user_desig: form.designation,
        email_id: actualEmail,
        mobile: actualMobile,
        Password: form.password,
        Emailotp: emailOtpValue || "",
        Mobileotp: mobileOtpValue || "",
        EVerify_tag: emailOtpValue ? "Y" : "N",
        MVerify_tag: mobileOtpValue ? "Y" : "N",
      };
      console.log("payload", payload)
      const result = await insertUser(payload);
      if (result?.status === "1") {
        CustomSwal.fire("Success", "Account created successfully", "success").then(() =>
          navigate("/")
        );
      } else {
        CustomSwal.fire("Warning", result?.message, "warning");
      }
    } catch (error) {
      CustomSwal.fire("Error", "Something went wrong during signup", "error");
    }
  };

  const handleClear = () => {
    setForm({
      staffId: "",
      staffName: "",
      instId: "",
      loginId: "",
      department: "",
      designation: "",
      email: "",
      mobileNo: "",
      password: "",
      repassword: "",
    });
    setFormSubmitted(false);
    setErrors({});
    setActualEmail("");
    setActualMobile("");
    setOtpCache(null);
    setVerifiedEmailOtp("");
    setVerifiedMobileOtp("");
  };

  return (
    <div className="full-background">
      <video autoPlay muted loop playsInline className="bg-video">
        <source src="/assets/BgVideo.mp4" type="video/mp4" />
      </video>

      <div className="signup-card">
        <div className="signup-header-bar">
          <h4 className="signup-title">Sign Up</h4>
        </div>

        <form onSubmit={handleSubmit} className="signupform" autoComplete="off">
          {formSubmitted && Object.keys(errors).length > 0 && (
            <span className="form-errors">
              <p className="error-msg">Please fill in required fields correctly.</p>
            </span>
          )}

          {/* Staff ID & Institution */}
          <div className="formGroup">
            <div className="floating-label-group">
              <i className="fas fa-solid fa-id-card input-icon"></i>
              {/* Staff ID Field */}
              <input
                type="text"
                id="staffId"
                placeholder=" "
                value={form.staffId}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow only letters, numbers, and underscore
                  if (/^[a-zA-Z0-9_]*$/.test(value)) {
                    handleChange("staffId", value);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Tab" && form.staffId.trim() !== "") fetchStaffDetails();
                }}
              />
              <label htmlFor="staffId" className={errors.staffId ? 'text-danger' : ''}>
                Staff ID
              </label>
            </div>

            <div className="floating-label-group">
              <i className="fas fa-solid fa-institution input-icon"></i>
              <input type="text" id="Institution" placeholder=" " value={form.instId} readOnly />
              <label htmlFor="Institution" className={errors.instId ? 'text-danger' : ''}>
                Institution
              </label>
            </div>
          </div>

          {/* loginId & staffName */}
          <div className="formGroup">


            <div className="floating-label-group">
              <i className="fas fa-solid fa-user input-icon"></i>
              <input type="text" id="staffName" placeholder=" " value={form.staffName} readOnly />
              <label htmlFor="staffName" className={errors.staffName ? 'text-danger' : ''}>
                Staff Name
              </label>
            </div>
            <div className="floating-label-group">
              <i className="fas fa-solid fa-building input-icon"></i>
              <input type="text" id="department" placeholder=" " value={form.department} readOnly />
              <label htmlFor="department" className={errors.department ? 'text-danger' : ''}>
                Department
              </label>
            </div>
          </div>

          {/* department & designation */}
          <div className="formGroup">

            <div className="floating-label-group">
              <i className="fas fa-solid fa-briefcase input-icon"></i>
              <input type="text" id="designation" placeholder=" " value={form.designation} readOnly />
              <label htmlFor="designation" className={errors.designation ? 'text-danger' : ''}>
                Designation
              </label>
            </div>

            <div className="floating-label-group">
              <i className="fas fa-solid fa-envelope input-icon"></i>
              <input type="email" id="email" placeholder=" " value={form.email} readOnly />
              <label htmlFor="email" className={errors.email ? 'text-danger' : ''}>
                Email
              </label>
            </div>
          </div>

          {/* email & Mobile */}
          <div className="formGroup">

            <div className="floating-label-group">
              <i className="fas fa-solid fa-user-circle input-icon"></i>
              {/* Login ID Field */}
              <input
                type="text"
                id="loginId"
                placeholder=" "
                value={form.loginId}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^[a-zA-Z0-9_]*$/.test(value)) handleChange("loginId", value);
                }}
                onBlur={async () => {
                  const value = form.loginId.trim();
                  if (!value) return;

                  try {
                    const res: any = await checkLoginIdExists(value); // decrypted response
                    if (res?.Status === "2") { // '2' indicates login ID already exists
                      await CustomSwal.fire(
                        "Login ID Already Exists",
                        "Please give another one.",
                        "warning"
                      );
                      handleChange("loginId", ""); // clear invalid login ID
                    }
                  } catch (err) {
                    console.error("Login ID check error:", err);
                  }
                }}
              />
              <label htmlFor="loginId" className={errors.loginId ? 'text-danger' : ''}>
                Login Id
              </label>
            </div>

            <div className="floating-label-group">
              <i className="fas fa-solid fa-mobile-screen-button input-icon"></i>
              <input type="text" id="mobileNo" placeholder=" " value={form.mobileNo} readOnly />
              <label htmlFor="mobileNo" className={errors.mobileNo ? 'text-danger' : ''}>
                Mobile No
              </label>
            </div>
          </div>

          {/* Password & Confirm Password */}
          <div className="formGroup">
            <div className="floating-label-group">
              <i className="fas fa-solid fa-lock input-icon"></i>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                placeholder=" "
                value={form.password}
                onChange={(e) => handleChange('password', e.target.value.replace(/\s/g, ''))}
              />
              <label htmlFor="password" className={errors.password ? 'text-danger' : ''}>
                New Password <span className="label-with-error">
                  {form.password && (
                    <>
                      {passwordTooShort && (
                        <span className="error-text">Password must be at least 6 characters.</span>
                      )}
                    </>
                  )}
                </span>
              </label>
              <i
                className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'} toggle-password-icon`}
                onClick={() => setShowPassword(!showPassword)}
                title="Toggle Password"
              ></i>
            </div>
            <div className="floating-label-group">
              <i className="fas fa-solid fa-lock input-icon"></i>
              <input
                type={showPassword ? 'text' : 'password'}
                id="repassword"
                placeholder=" "
                value={form.repassword}
                onChange={(e) => handleChange('repassword', e.target.value.replace(/\s/g, ''))}
              />
              <label htmlFor="repassword" className={errors.repassword ? 'text-danger' : ''}>
                Confirm Password <span className="label-with-error">
                  {form.repassword && (
                    <>
                      {!passwordsMatch && !passwordTooShort && (
                        <span className="error-text">Passwords do not match.</span>
                      )}
                    </>
                  )}
                </span>
              </label>
            </div>
          </div>
          <div className="form-button">
            <button type="submit" className="form-btn signup-btn">
              Create
            </button>
            <button type="button" className="form-btn clear-btn" onClick={handleClear}>
              Clear
            </button>
          </div>

          <div className="login-link">
            Already have an account? <Link to="/">Login</Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Signup;
